import React, { useRef, useState } from 'react';
import { LayoutDocument, LayoutElement, LayoutKind, insideHall, layoutKinds, validateLayout } from '../utils/venueLayout';
import './VenueLayoutEditor.css';

interface Props {
  value: LayoutDocument;
  onChange: (value: LayoutDocument) => void;
  hallEditable?: boolean;
  disabled?: boolean;
}
type Gesture = { pointerId: number; element: LayoutElement; x: number; y: number; mode: 'move' | 'resize' | 'rotate' };

export default function VenueLayoutEditor({ value, onChange, hallEditable = false, disabled = false }: Props) {
  const [selectedId, setSelectedId] = useState<string>();
  const [kind, setKind] = useState<LayoutKind>('table');
  const [notice, setNotice] = useState('');
  const [touchMode, setTouchMode] = useState<Gesture['mode']>('move');
  const svg = useRef<SVGSVGElement>(null);
  const gesture = useRef<Gesture | undefined>(undefined);
  const selected = value.elements.find(e => e.id === selectedId);
  const error = validateLayout(value);
  const update = (element: LayoutElement) => {
    onChange({ ...value, elements: value.elements.map(e => e.id === element.id ? element : e) });
  };
  const point = (event: React.PointerEvent) => {
    const node = svg.current!;
    const matrix = node.getScreenCTM();
    if (!matrix) return null;
    const p = node.createSVGPoint(); p.x = event.clientX; p.y = event.clientY;
    return p.matrixTransform(matrix.inverse());
  };
  const start = (event: React.PointerEvent<SVGElement>, element: LayoutElement, mode: Gesture['mode']) => {
    if (disabled) return;
    const p = point(event); if (!p) return;
    event.preventDefault(); event.stopPropagation();
    setSelectedId(element.id);
    event.currentTarget.focus();
    svg.current!.setPointerCapture(event.pointerId);
    gesture.current = { pointerId: event.pointerId, element: { ...element }, x: p.x, y: p.y, mode };
  };
  const move = (event: React.PointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== event.pointerId || disabled) return;
    const p = point(event); if (!p) return;
    const e = { ...g.element };
    const dx = p.x - g.x; const dy = p.y - g.y;
    if (g.mode === 'move') { e.x += dx; e.y += dy; }
    if (g.mode === 'resize') {
      const angle = e.rotation * Math.PI / 180;
      e.width = Math.max(0.1, e.width + 2 * (dx * Math.cos(angle) + dy * Math.sin(angle)));
      e.height = Math.max(0.1, e.height + 2 * (-dx * Math.sin(angle) + dy * Math.cos(angle)));
      e.x = g.element.x + (g.element.width - e.width) / 2;
      e.y = g.element.y + (g.element.height - e.height) / 2;
    }
    if (g.mode === 'rotate') {
      e.rotation = (Math.atan2(p.y - e.y - e.height / 2, p.x - e.x - e.width / 2) * 180 / Math.PI + 90 + 360) % 360;
    }
    if (insideHall(e, value) && e.width <= 100 && e.height <= 100) { update(e); setNotice(''); }
    else setNotice('Рух обмежено межами залу.');
  };
  const add = () => {
    if (value.elements.length >= 300) return;
    const size = Math.min(1, value.width, value.height);
    const element: LayoutElement = {
      id: crypto.randomUUID(), kind, label: layoutKinds[kind], x: (value.width - size) / 2,
      y: (value.height - size) / 2, width: size, height: size, rotation: 0,
      seats: kind === 'seat' ? 1 : kind === 'table' ? 4 : 0,
    };
    onChange({ ...value, elements: [...value.elements, element] }); setSelectedId(element.id);
  };
  const numberInput = (label: string, n: number, change: (n: number) => void, min: number, max: number, step = 0.1) =>
    <label>{label}<input type="number" value={Number.isFinite(n) ? n : ''} min={min} max={max}
      step={step} disabled={disabled} onChange={e => change(e.target.value === '' ? NaN : Number(e.target.value))} /></label>;
  const safeHall = Number.isFinite(value.width) && value.width >= 0.1 && Number.isFinite(value.height) && value.height >= 0.1;
  return <section className="venue-layout-editor" aria-label="Візуальний план залу">
    <p>Розміри у метрах. Перетягніть елемент; стрілки рухають на 0,1 м (Shift: 1 м).
      Квадратний маркер змінює розмір, круглий — поворот. Усі значення можна ввести нижче. Зони можуть перекриватися.</p>
    {hallEditable ? <div className="layout-fields">
      {numberInput('Ширина залу, м', value.width, width => onChange({ ...value, width }), 0.1, 100)}
      {numberInput('Довжина залу, м', value.height, height => onChange({ ...value, height }), 0.1, 100)}
      {numberInput('Місткість залу', value.capacity, capacity => onChange({ ...value, capacity }), 1, 5000, 1)}
    </div> : <p>Зал: {value.width} × {value.height} м. Місткість: {value.capacity}.</p>}
    <div className="layout-actions" role="group" aria-label="Режим дотику">
      {(['move', 'resize', 'rotate'] as const).map(mode => <button type="button" key={mode}
        disabled={disabled} aria-pressed={touchMode === mode} onClick={() => setTouchMode(mode)}>
        {mode === 'move' ? 'Переміщення' : mode === 'resize' ? 'Зміна розміру' : 'Поворот'}
      </button>)}
    </div>
    {safeHall && <svg ref={svg} className="layout-hall" viewBox={`0 0 ${value.width} ${value.height}`}
      role="group" aria-label="План залу" onPointerMove={move}
      onPointerUp={() => { gesture.current = undefined; }} onPointerCancel={() => { gesture.current = undefined; }}>
      <rect className="layout-floor" width={value.width} height={value.height} />
      {value.elements.filter(e => [e.x, e.y, e.width, e.height, e.rotation].every(Number.isFinite) && e.width > 0 && e.height > 0).map(e =>
        <g key={e.id} transform={`translate(${e.x + e.width / 2} ${e.y + e.height / 2}) rotate(${e.rotation})`}
          tabIndex={disabled ? -1 : 0} role="button" aria-label={`${e.label}, ${layoutKinds[e.kind]}, ${e.seats} місць`}
          aria-pressed={e.id === selectedId} onFocus={() => setSelectedId(e.id)}
          onPointerDown={event => start(event, e, touchMode)} onKeyDown={event => {
            if (disabled || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
            event.preventDefault();
            const step = event.shiftKey ? 1 : 0.1;
            const next = { ...e, x: e.x + (event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0),
              y: e.y + (event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0) };
            if (insideHall(next, value)) { update(next); setNotice(''); } else setNotice('Рух обмежено межами залу.');
          }}>
          <rect className={`layout-element layout-${e.kind} ${e.id === selectedId ? 'layout-selected' : ''}`}
            x={-e.width / 2} y={-e.height / 2} width={e.width} height={e.height} />
          <text textAnchor="middle" dominantBaseline="middle" fontSize={Math.min(e.width / 7, e.height / 4, 0.4)}>
            {e.label.length > 22 ? `${e.label.slice(0, 22)}…` : e.label}
          </text>
          {e.id === selectedId && !disabled && <>
            <rect className="layout-handle" x={e.width / 2 - 0.15} y={e.height / 2 - 0.15} width={0.3} height={0.3}
              onPointerDown={event => start(event, e, 'resize')} />
            <circle className="layout-handle" cx={0} cy={-e.height / 2} r={0.15}
              onPointerDown={event => start(event, e, 'rotate')} />
          </>}
        </g>)}
    </svg>}
    <p role="status">{notice}</p>
    {error && <p role="alert">{error}</p>}
    <div className="layout-fields">
      <label>Тип нового елемента<select value={kind} disabled={disabled} onChange={e => setKind(e.target.value as LayoutKind)}>
        {Object.entries(layoutKinds).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select></label>
      <button type="button" disabled={disabled || !safeHall || value.elements.length >= 300} onClick={add}>Додати елемент</button>
      <label>Обраний елемент<select value={selectedId ?? ''} onChange={e => setSelectedId(e.target.value)}>
        <option value="">Оберіть елемент</option>
        {value.elements.map(e => <option key={e.id} value={e.id}>{e.label} ({layoutKinds[e.kind]})</option>)}
      </select></label>
    </div>
    {selected && <fieldset disabled={disabled}>
      <legend>Елемент: {selected.label}</legend>
      <div className="layout-fields">
        <label>Назва<input value={selected.label} maxLength={120} onChange={e => update({ ...selected, label: e.target.value })} /></label>
        <label>Тип<select value={selected.kind} onChange={e => {
          const nextKind = e.target.value as LayoutKind;
          update({ ...selected, kind: nextKind, seats: nextKind === 'seat' ? 1 : nextKind === 'table' ? selected.seats : 0 });
        }}>{Object.entries(layoutKinds).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
        {numberInput('X, м', selected.x, x => update({ ...selected, x }), 0, value.width)}
        {numberInput('Y, м', selected.y, y => update({ ...selected, y }), 0, value.height)}
        {numberInput('Ширина, м', selected.width, width => update({ ...selected, width }), 0.1, 100)}
        {numberInput('Довжина, м', selected.height, height => update({ ...selected, height }), 0.1, 100)}
        {numberInput('Поворот, °', selected.rotation, rotation => update({ ...selected, rotation }), 0, 359.9)}
        {selected.kind === 'table' && numberInput('Місця за столом', selected.seats, seats => update({ ...selected, seats }), 0, 500, 1)}
      </div>
      <button type="button" className="layout-delete" onClick={() => {
        onChange({ ...value, elements: value.elements.filter(e => e.id !== selected.id) }); setSelectedId(undefined);
      }}>Видалити елемент</button>
    </fieldset>}
    <p>Елементів: {value.elements.length}/300. Місць: {value.elements.reduce((sum, e) => sum + e.seats, 0)}.</p>
  </section>;
}
